/**
 * 运行时对象注册表（P0-3c）：对象 id → 场景图引用。
 *
 * 为什么独立于 zustand store：
 *  Object3D 引用**绝不能**进可序列化的工程状态（会造成循环引用 + 每次拖拽
 *  触发全量 re-render），store 只存数据，这里存"数据对应的那坨场景图对象"。
 *  gizmo 附着、姿势应用、拾取高亮都要同步取用，非响应式反而正确。
 *
 * 生命周期：SceneObject 挂载时 register、卸载时 unregister——
 *  泄漏防护点：删对象/切页面必走卸载，注册表残留会让 gizmo 附着到已 dispose 的对象上。
 */
import type { Object3D } from 'three';

import type { RestSnapshot } from './poseApply';

export interface RuntimeEntry {
  /** store 里 transform 绑定的外层 group（gizmo 附着目标） */
  group: Object3D;
  /** 模型场景根（姿势应用目标；几何体对象为 null） */
  root: Object3D | null;
  /** 加载时捕获的 rest 快照（几何体对象为空表） */
  rest: RestSnapshot;
  /**
   * 加载时（挂载前、未受活体蒙皮污染）算好的可靠包围盒，root 局部空间。
   * 选中框用它而非选中时对活体 Box3.setFromObject——后者对克隆蒙皮体不可靠（D-28）。
   */
  bounds?: { center: [number, number, number]; size: [number, number, number] } | undefined;
}

const registry = new Map<string, RuntimeEntry>();

/** 变更订阅：模型异步加载完成/对象卸载时通知 React 侧（gizmo 附着依赖引用就绪） */
type Listener = () => void;
const listeners = new Set<Listener>();

export function subscribeRuntime(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function notify(): void {
  for (const fn of listeners) {
    fn();
  }
}

export function registerRuntime(id: string, entry: RuntimeEntry): void {
  registry.set(id, entry);
  notify();
}

export function unregisterRuntime(id: string): void {
  registry.delete(id);
  notify();
}

/** 手动触发变更通知（如套姿势后包围盒变了，选中框需要重算） */
export function touchRuntime(): void {
  notify();
}

export function getRuntime(id: string): RuntimeEntry | null {
  return registry.get(id) ?? null;
}

/** 仅供测试：清空注册表 */
export function __resetRuntimeRegistry(): void {
  registry.clear();
}
